import React, { createContext, useContext, useState, useEffect } from 'react';
import onboardingConfig from '../config/onboarding-config.json';

const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }) => {
  const [currentScenario, setCurrentScenario] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [lastSeenDate, setLastSeenDate] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Carrega data da última visualização ao montar
  useEffect(() => {
    const stored = localStorage.getItem('onboarding_last_seen');
    if (stored) {
      setLastSeenDate(new Date(stored));
    }
    // Marca como inicializado após carregar do localStorage
    setIsInitialized(true);
  }, []);

  // Determina o cenário baseado nas estatísticas do usuário
  const determineScenario = (stats) => {
    const { professionals = 0, services = 0, places = 0, schedules = 0 } = stats;

    // Todos os recursos cadastrados
    if (professionals > 0 && services > 0 && places > 0 && schedules > 0) {
      return null; // Sem onboarding necessário
    }

    // Pronto para agenda
    if (professionals > 0 && services > 0 && places > 0 && schedules === 0) {
      return onboardingConfig.scenarios['ready-for-agenda'];
    }

    // Tem profissional e serviço
    if (professionals > 0 && services > 0 && places === 0) {
      return onboardingConfig.scenarios['has-professional-and-service'];
    }

    // Tem apenas profissional
    if (professionals > 0 && services === 0) {
      return onboardingConfig.scenarios['has-professional'];
    }

    // Completamente vazio
    if (professionals === 0) {
      return onboardingConfig.scenarios['empty'];
    }

    return null;
  };

  // Atualiza estatísticas e verifica se deve mostrar welcome
  const updateStats = (stats) => {
    setUserStats(stats);
    const scenario = determineScenario(stats);
    setCurrentScenario(scenario);

    // CRÍTICO: Só verifica se deve mostrar após inicialização completa
    // E só se realmente tiver um cenário válido
    if (!isInitialized || !scenario) {
      // Garante que está fechado durante inicialização
      setShowWelcome(false);
      return;
    }

    // Verifica se deve mostrar o welcome
    const daysSinceLastSeen = lastSeenDate
      ? Math.floor((new Date() - lastSeenDate) / (1000 * 60 * 60 * 24))
      : 999;

    const shouldShow =
      daysSinceLastSeen >= onboardingConfig.settings.showWelcomeAfterDays;

    setShowWelcome(shouldShow);
  };

  // Marca como visto
  const markAsSeen = () => {
    const now = new Date().toISOString();
    localStorage.setItem('onboarding_last_seen', now);
    setLastSeenDate(new Date(now));
    setShowWelcome(false);
  };

  // Força mostrar welcome
  const forceShowWelcome = () => {
    setShowWelcome(true);
  };

  // Pega hint para uma página específica
  const getPageHint = (pageName, pageStats = {}) => {
    const hints = onboardingConfig.pageHints[pageName] || [];
    const combinedStats = { ...userStats, ...pageStats };

    // Encontra o primeiro hint que satisfaz a condição
    for (const hint of hints) {
      if (matchesCondition(hint.condition, combinedStats)) {
        return hint;
      }
    }

    return null;
  };

  // Verifica se estatísticas satisfazem uma condição
  const matchesCondition = (condition, stats) => {
    return Object.entries(condition).every(([key, value]) => {
      const statValue = stats[key] || 0;

      if (typeof value === 'string' && value.startsWith('>')) {
        const threshold = parseInt(value.substring(1));
        return statValue > threshold;
      }

      if (typeof value === 'string' && value.startsWith('<')) {
        const threshold = parseInt(value.substring(1));
        return statValue < threshold;
      }

      return statValue === value;
    });
  };

  const value = {
    currentScenario,
    showWelcome,
    userStats,
    config: onboardingConfig,
    updateStats,
    markAsSeen,
    forceShowWelcome,
    getPageHint,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};
